import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { FeedbackThread } from "../components/ConversationThread";
import { MessageSquare, FileText, UserX, HelpCircle } from "lucide-react";
import { toast } from "sonner";

function ContextLabel({ f }) {
  if (f.source_role === "worker" || f.is_question) {
    return <span className="text-xs font-medium text-[#9CA3AF] flex items-center gap-1"><HelpCircle size={12} /> Question{f.document_title ? ` · ${f.document_title}` : ""}</span>;
  }
  if (f.document_id) {
    return <span className="text-xs font-medium text-[#9CA3AF] flex items-center gap-1"><FileText size={12} /> {f.document_title}</span>;
  }
  return <span className="text-xs font-medium text-[#9CA3AF]">General feedback</span>;
}

function ReviewFeedbackItem({ f, readOnly, onChanged }) {
  const send = async (text) => {
    await api.post(`/feedback/${f.id}/replies`, { text });
    toast.success("Reply sent to 360 Safety Systems");
    onChanged();
  };
  return (
    <div data-testid={`review-feedback-${f.id}`} className="rounded-xl border p-5"
      style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <span className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap">
          {f.anonymous ? <><UserX size={14} className="text-[#9CA3AF]" /> Anonymous</> : f.name}
          <ContextLabel f={f} />
          {f.client_unread && (
            <span data-testid={`review-feedback-new-${f.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ backgroundColor: "#F97316", color: "#fff" }}>New reply</span>
          )}
        </span>
        <span className="text-xs text-[#9CA3AF]">{new Date(f.created_at).toLocaleString()}</span>
      </div>
      {f.feedback_text && <p className="text-sm text-[#cbd5e1] leading-relaxed">{f.feedback_text}</p>}
      <FeedbackThread replies={f.replies} onSend={readOnly ? null : send} dark testPrefix={`review-feedback-${f.id}`} />
    </div>
  );
}

export function ReviewFeedbackTab({ clientId, readOnly, onChanged }) {
  const [feedback, setFeedback] = useState(null);
  const markedRef = useRef(new Set());

  useEffect(() => {
    api.get(`/clients/${clientId}/feedback`).then(({ data }) => {
      setFeedback(data);
      if (readOnly) return;
      const toMark = data.filter((f) => f.client_unread && !markedRef.current.has(f.id));
      if (toMark.length) {
        toMark.forEach((f) => markedRef.current.add(f.id));
        Promise.all(toMark.map((f) => api.post(`/feedback/${f.id}/client-read`).catch(() => {})))
          .then(() => onChanged && onChanged());
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const reload = () => api.get(`/clients/${clientId}/feedback`).then(({ data }) => setFeedback(data));

  return (
    <section data-testid="review-feedback-tab">
      <h1 className="font-heading text-white text-3xl font-bold">Feedback & Replies</h1>
      <p className="text-[#9CA3AF] mt-1 mb-8">
        Feedback and questions raised by your organisation, with replies from 360 Safety Systems. You can respond to any thread here.
      </p>
      {feedback === null && <p className="text-[#9CA3AF]" data-testid="review-feedback-loading">Loading…</p>}
      {feedback?.length === 0 && (
        <div className="text-center py-20 text-[#9CA3AF]" data-testid="review-feedback-empty">
          <MessageSquare size={40} className="mx-auto mb-3 opacity-40" />
          No feedback submitted yet.
        </div>
      )}
      <div className="space-y-4">
        {(feedback || []).map((f) => (
          <ReviewFeedbackItem key={f.id} f={f} readOnly={readOnly} onChanged={() => { reload(); onChanged && onChanged(); }} />
        ))}
      </div>
    </section>
  );
}
